<script setup lang="ts">
/**
 * Fiat-transaction detail container.
 *
 * Subscribes to the detail query keyed off the `:id` param, wires the
 * breadcrumb / back-link, owns loading + error states, and renders the
 * presentational `FiatTransactionDetail` body inside the page shell.
 */
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import Card from '@uiKit/components/Card.vue'
import Message from '@uiKit/elements/Message.vue'
import PageNav from '@uiKit/components/PageNav.vue'

import { routes, routeNames } from '@app/router/routes'

import FiatTransactionDetail from '../components/FiatTransactionDetail.vue'
import { useFiatTransactionDetailQuery } from '../api/queries/useFiatTransactionDetailQuery'

const route = useRoute()
const router = useRouter()

const transactionId = computed(() => String(route.params.id ?? ''))

const { data: details, isLoading, error, refetch } = useFiatTransactionDetailQuery(transactionId)

const SHORT_ID_LENGTH = 8

const shortId = computed(() => {
  const id = transactionId.value
  if (!id) return 'Transaction'
  return id.length > SHORT_ID_LENGTH ? id.slice(0, SHORT_ID_LENGTH) : id
})

const breadcrumbs = computed(() => [
  { label: 'All transactions', onClick: () => router.push(routes.TREASURY_FIAT_TRANSACTIONS_LIST) },
  { label: shortId.value },
])

function handleBack() {
  router.push(routes.TREASURY_FIAT_TRANSACTIONS_LIST)
}

function openOrderDetail(orderId: string) {
  router.push({ name: routeNames.ORDERS_ALL_DETAIL, params: { id: orderId } })
}

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}

const NOT_FOUND_CODE = 'FIAT_TRANSACTION_NOT_FOUND'

const isNotFound = computed(() => {
  if (!error.value) return false
  const message = describeError(error.value)
  const code = (error.value as { code?: string })?.code
  return message.includes(NOT_FOUND_CODE) || code === NOT_FOUND_CODE
})
</script>

<template>
  <Box class="page">
    <PageNav :crumbs="breadcrumbs" :on-back="handleBack" />

    <Box class="page__body">
      <!-- Loading skeleton -->
      <Card v-if="isLoading" variant="outlined" padding="4" class="detail-card">
        <Box flex direction="col" gap="3">
          <Skeleton width="160px" height="22px" border-radius="6px" />
          <Skeleton width="240px" height="38px" border-radius="6px" />
          <Skeleton width="100%" height="120px" border-radius="8px" />
          <Skeleton width="100%" height="160px" border-radius="8px" />
          <Skeleton width="100%" height="200px" border-radius="8px" />
        </Box>
      </Card>

      <!-- Not-found banner -->
      <Card v-else-if="isNotFound" variant="outlined" padding="4" class="detail-card">
        <Message color="danger" variant="outlined">
          <Box flex direction="col" gap="2">
            <Text type="body" weight="semibold">Transaction not found</Text>
            <Text type="body-sm" color="muted">
              We couldn't find a transaction with id {{ transactionId }}. It may have been removed
              or the link is incorrect.
            </Text>
            <Box flex>
              <Button
                label="Back to all transactions"
                color="primary"
                variant="outline"
                @click="handleBack"
              />
            </Box>
          </Box>
        </Message>
      </Card>

      <!-- Generic error banner -->
      <Card v-else-if="error" variant="outlined" padding="4" class="detail-card">
        <Message color="danger" variant="outlined">
          <Box flex justify="between" align="center" gap="3">
            <Text type="body-sm">
              Could not load this transaction. {{ describeError(error) }}
            </Text>
            <Button label="Try again" variant="text" color="danger" @click="refetch()" />
          </Box>
        </Message>
      </Card>

      <!-- Content -->
      <Card v-else-if="details" variant="outlined" padding="4" class="detail-card">
        <FiatTransactionDetail :details="details" @open-order="openOrderDetail" />
      </Card>
    </Box>
  </Box>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.page__body {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  min-height: 0;
  padding: 0 1.5rem 2.5rem;
}

.page__body::-webkit-scrollbar {
  display: none;
}

.detail-card {
  overflow: hidden;
}
</style>
